import React from "react";
import { connect } from "react-redux";
import { Guid } from "guid-typescript";

import Tooltip from "../../components/tooltip/Tooltip";
import Header from "../../components/header/Header";
import Body from "../../components/body/Body";
import { AuthJWTPayload } from "../../types/system/AuthJWT";
import { IAppState } from "../../state/Store";
import Sidebar from "../sidebar/Sidebar";
import {
    resetConfiguration,
    setDisplayMenuOpen,
    setPageLoadingOverlay,
    setPrintMenuOpen,
    setShareMenuOpen,
} from "../../state/system/UIConfiguration";
import MessagingDialog from "../messaging/MessagingDialog";
import AppState from "../../foundation/AppState";
import NotificationsOverlay from "../notifications/NotificationsOverlay";
import { resetRouteDisplay } from "../../state/system/Display";
import { withRouter } from "../routing/withRouter";
import ContextMenuContextContainer from "../contextmenu/ContextMenuContext";
import ReauthDialog from "../reauthenticate/ReauthDialog";
import { MockPromiseWrapper } from "../../types/system/MockPromiseWrapper";
import Datastore from "../../communication/Datastore";
import ErrorBoundary from "../error/ErrorBoundary";
import { initialiseImports } from "../../state/files/Imports";
import { FileUploadState } from "../../state/files/Folders";
import File from "../../types/files/File";
import Status from "../status/Status";
import { InitializeStatusHub } from "../../sockets/statusManager";
import { initialiseSettingsDetails } from "../../state/system/Settings";
import { AppContext } from "../../types/system/AppContext";

import packageInfo from "../../../../package.json";

import "./Main.scss";

interface MainProps {
    location: string;
    displayMenuOpen: boolean;
    printMenuOpen: boolean;
    shareMenuOpen: boolean;
    pageLoadingOverlay: boolean;

    appLogo: string;
    flatLogo: string;

    uploads?: FileUploadState[];
    imports?: File[];

    jwt: AuthJWTPayload;
    file: string;
    setDisplayMenuOpen: Function;
    setShareMenuOpen: Function;
    setPrintMenuOpen: Function;
    resetConfiguration: Function;
    resetRouteDisplay: Function;

    navigate: (path: string) => void;
    setPageLoadingOverlay: (value: boolean) => void;
    initialiseSettingsDetails: () => Promise<void>;
    initialiseImports: () => Promise<void>;
}

interface MainState {
    version: number;
    reauthOpen: boolean;
    elevioOpen: boolean;
    dropGrids: ((position: { x: number; y: number }, data?: any) => void)[];
    reauthPromise: MockPromiseWrapper<boolean>;
}

class Main extends React.Component<MainProps, MainState> {
    state = {
        version: 1,
        reauthOpen: false,
        elevioOpen: false,
        dropGrids: [],
        reauthPromise: null,
    };

    async componentDidMount() {
        this.initialiseElevio(this.props);
        this.reinitialise();
    }

    componentDidUpdate(
        prevProps: Readonly<MainProps>,
        prevState: Readonly<{}>,
        snapshot?: any,
    ): void {
        if (prevProps.location !== this.props.location) {
            this.props.resetRouteDisplay();
        }
    }

    reinitialise() {
        InitializeStatusHub();
        this.props.setPageLoadingOverlay(false);
        this.props.initialiseImports();
        this.props.initialiseSettingsDetails();
        InitializeStatusHub();
    }

    initialiseElevio(props: MainProps) {
        if (window["_elev"].on && props.jwt && !window["_elevInit"]) {
            window["_elevInit"] = true;

            window["_elev"].on("widget:opened", _elev => {
                // props.elevioChanged?.(true);
                window["elevioOpen"] = true;
            });
            window["_elev"].on("widget:closed", _elev => {
                // props.elevioChanged?.(false);
                window["elevioOpen"] = false;
            });
            window["_elev"].on("load", function (_elev) {
                _elev.setSettings({
                    articleFeedbackEnabled: true,
                    cspStrictCompatibility: true,
                    disableDevelopmentWarnings: true,
                    disablePushState: true,
                    display_type: "full",
                    docked_position: "floor",
                    enabled: true,
                    hideLauncher: true,
                    menuShowUntilClosed: true,
                    pushin: true,
                    main_color: "#09c",
                    reinitializeOnUrlChange: false,
                    tab_teaser: "",
                    side: "right",
                });
                _elev.setLanguage("en-gb");
                _elev.setTranslations({
                    help: "Help & Support",
                });
                _elev.setUser({
                    first_name: props.jwt.given_name,
                    last_name: props.jwt.family_name,
                    email: props.jwt.email,
                    user_hash: props.jwt.elevioUserHash,
                    registered_at: props.jwt.elevioUserRegisteredAt,
                });
            });
        }
    }

    setDisplayMenuOpen(value: boolean) {
        this.props.setDisplayMenuOpen(value);
    }
    setShareMenuOpen(value: boolean) {
        this.props.setShareMenuOpen(value);
    }
    setPrintMenuOpen(value: boolean) {
        this.props.setPrintMenuOpen(value);
    }
    setElevioOpen(value: boolean) {
        this.setState(() => ({ elevioOpen: value }));
    }

    reauthenticate() {
        const promise = new MockPromiseWrapper<boolean>(
            Guid.create().toString(),
            "CredentialCheck",
            () => this.setState({ reauthPromise: null }),
        );
        this.setState({
            reauthPromise: promise,
        });

        (async () => {
            const required = await Datastore.postType<boolean>(
                "api/Authentication/ShouldVerify",
            );
            if (required) this.setState(() => ({ reauthOpen: true }));
            else promise?.resolve(true);
        })();

        return promise.promise;
    }

    reauthenticateSuccess() {
        this.setState(() => ({ reauthOpen: false }));
        this.state.reauthPromise?.resolve(true);
    }

    reauthenticateCancelled() {
        this.setState(() => ({ reauthOpen: false }));
        this.state.reauthPromise?.reject(false);
    }

    render(): React.ReactNode {
        const contextValue = {
            navigate: (path: string) => {
                this.props.navigate(path);
            },
            refreshRoute: () => {
                this.reinitialise();
                this.setState(prevState => ({
                    version: prevState.version + 1,
                }));
            },
            registerDropGrid: (
                handler: (
                    position: { x: number; y: number },
                    data?: any,
                ) => void,
            ) => {
                this.setState(prevState => ({
                    dropGrids: [...prevState.dropGrids, handler],
                }));
            },
            deregisterDropGrid: (
                handler: (
                    position: { x: number; y: number },
                    data?: any,
                ) => void,
            ) => {
                this.setState(prevState => ({
                    dropGrids: prevState.dropGrids.filter(
                        item => item !== handler,
                    ),
                }));
            },
            evaluateDropGrids: (
                position: { x: number; y: number },
                data?: any,
            ) => {
                this.state.dropGrids.forEach(handler =>
                    handler(position, data),
                );
            },
            verifyCredentials: this.reauthenticate.bind(this),
        };

        return (
            <div
                className={`page-main ${
                    !this.props.pageLoadingOverlay ? "loaded" : ""
                }`}>
                <div className="accents">
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
                <div
                    className="app-load-icon"
                    style={{ backgroundImage: `url(${this.props.appLogo})` }}>
                    <div className="app-load-message">
                        Loading {packageInfo.friendlyName}...
                    </div>
                </div>
                <AppContext.Provider value={contextValue}>
                    <ContextMenuContextContainer
                        refreshRoute={contextValue.refreshRoute}>
                        {/* Overlay component for popup notifications */}
                        <ErrorBoundary sectionName="Notifications Overlay">
                            <NotificationsOverlay></NotificationsOverlay>
                        </ErrorBoundary>

                        <div
                            className={`page-frame${
                                this.state.elevioOpen ? " elevio-open" : ""
                            }`}>
                            <ErrorBoundary sectionName="Main Header">
                                <Header
                                    appLogo={this.props.appLogo}
                                    flatLogo={this.props.flatLogo}
                                />
                            </ErrorBoundary>
                            <div className="print-only left-buffer-small bottom-buffer">
                                There is no content to display.
                            </div>

                            <ErrorBoundary sectionName="Main Body">
                                <Body
                                    version={this.state.version}
                                    displayOpen={this.props.displayMenuOpen}
                                    displayClosed={() =>
                                        this.setDisplayMenuOpen(false)
                                    }
                                    shareOpen={this.props.shareMenuOpen}
                                    shareClosed={() =>
                                        this.setShareMenuOpen(false)
                                    }
                                    printOpen={this.props.printMenuOpen}
                                    printClosed={() =>
                                        this.setPrintMenuOpen(false)
                                    }
                                />
                            </ErrorBoundary>

                            <ErrorBoundary sectionName="Tooltips">
                                <Tooltip></Tooltip>
                            </ErrorBoundary>
                        </div>

                        <div
                            className="mobile-pin no-print"
                            ng-click="actions.toggleSidebar()">
                            <div></div>
                            <div></div>
                            <div></div>
                        </div>

                        <ErrorBoundary sectionName="Sidebar">
                            <Sidebar
                                displayClicked={() => {
                                    this.setDisplayMenuOpen(
                                        !this.props.displayMenuOpen,
                                    );
                                }}
                                shareClicked={() => {
                                    this.setShareMenuOpen(
                                        !this.props.shareMenuOpen,
                                    );
                                }}
                            />
                        </ErrorBoundary>

                        <ErrorBoundary sectionName="Main Dialogs">
                            <MessagingDialog></MessagingDialog>
                            <ReauthDialog
                                open={this.state.reauthOpen}
                                title="Verify Credentials"
                                onSuccess={this.reauthenticateSuccess.bind(
                                    this,
                                )}
                                onCancel={this.reauthenticateCancelled.bind(
                                    this,
                                )}
                            />
                        </ErrorBoundary>

                        <Status />

                        <div
                            className={`app-load-overlay no-print${
                                !this.props.pageLoadingOverlay ? " loaded" : ""
                            }`}></div>
                    </ContextMenuContextContainer>
                </AppContext.Provider>
            </div>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { ui, folders, imports } = state.db;
    return {
        location: window.location.pathname,
        displayMenuOpen: ui.displayMenuOpen,
        shareMenuOpen: ui.shareMenuOpen,
        printMenuOpen: ui.printMenuOpen,
        pageLoadingOverlay: ui.pageLoadingOverlay,

        uploads: folders.uploads,
        imports: imports.imports,

        jwt: AppState.jwt,
        file: "",
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        setDisplayMenuOpen: (value: boolean) =>
            dispatch(setDisplayMenuOpen(value)),
        setShareMenuOpen: (value: boolean) => dispatch(setShareMenuOpen(value)),
        setPrintMenuOpen: (value: boolean) => dispatch(setPrintMenuOpen(value)),
        resetConfiguration: () => dispatch(resetConfiguration()),
        resetRouteDisplay: () => dispatch(resetRouteDisplay()),
        setPageLoadingOverlay: (value: boolean) =>
            dispatch(setPageLoadingOverlay(value)),
        initialiseSettingsDetails: () => dispatch(initialiseSettingsDetails()),
        initialiseImports: () => dispatch(initialiseImports()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Main));
